import type { PublicTopic } from './browseTypes';
import type { PrototypeRuntime } from './prototypeTypes';
import type { BrowseSort } from './browseData';

export interface BrowseViewCacheEntry {
	topics: PublicTopic[];
	rankingVersion?: string;
}

const entries = new Map<string, BrowseViewCacheEntry>();
const MAX_RANKING_VERSION_ENTRIES = 24;

/**
 * Browse results are display snapshots, never an authorization or data source.
 * The scope includes the trusted runtime identity so favorites/recent/votes do
 * not cross an account, session or plan boundary.
 */
export function browseViewScopeKey(runtime: PrototypeRuntime, filter: string, search: string, sort: BrowseSort = 'default'): string {
	const identity = runtime.sessionHash
		? `session:${runtime.sessionHash}`
		: runtime.userId
			? `user:${runtime.userId}`
			: 'visitor';
	return `${identity}|plan:${runtime.currentPlan ?? 'visitor'}|filter:${filter}|sort:${sort}|search:${search.trim().toLowerCase()}`;
}

export function browseViewCacheKey(runtime: PrototypeRuntime, filter: string, search: string, sort: BrowseSort = 'default', rankingVersion?: string): string {
	return `${browseViewScopeKey(runtime, filter, search, sort)}|ranking:${sort === 'trending' || sort === 'newest' ? rankingVersion ?? 'pending' : 'catalog'}`;
}

export function readBrowseViewCache(key: string): BrowseViewCacheEntry | null {
	const entry = entries.get(key);
	return entry ? { topics: entry.topics.slice(), rankingVersion: entry.rankingVersion } : null;
}

export function writeBrowseViewCache(key: string, topics: PublicTopic[], rankingVersion?: string): void {
	const entry = { topics: topics.slice(), rankingVersion };
	entries.set(key, entry);
	const aliasKey = rankingAliasKey(key, rankingVersion);
	if (aliasKey) entries.set(aliasKey, { topics: topics.slice(), rankingVersion });
	pruneRankingVersionEntries();
}

export function deleteBrowseViewCache(key: string): void {
	entries.delete(key);
}

/** Keeps a retained Browse snapshot aligned with a completed Topic reaction write. */
export function patchBrowseViewTopicViewerState(topicId: string, patch: Pick<PublicTopic, 'isFavorite' | 'voteValue'>): void {
	for (const [key, entry] of entries) {
		if (!entry.topics.some((topic) => topic.slug === topicId)) continue;
		entries.set(key, { ...entry, topics: entry.topics.map((topic) => topic.slug === topicId ? { ...topic, ...patch } : topic) });
	}
}

function rankingAliasKey(key: string, rankingVersion: string | undefined): string | null {
	if (!rankingVersion || !isRankingCacheKey(key)) return null;
	const versionSuffix = `|ranking:${rankingVersion}`;
	return key.endsWith(versionSuffix) ? key.slice(0, -versionSuffix.length) + '|ranking:pending' : null;
}

function pruneRankingVersionEntries(): void {
	const versionedKeys = [...entries.keys()].filter((key) => isRankingCacheKey(key) && !key.endsWith('|ranking:pending'));
	while (versionedKeys.length > MAX_RANKING_VERSION_ENTRIES) {
		const oldestKey = versionedKeys.shift();
		if (oldestKey) entries.delete(oldestKey);
	}
}

function isRankingCacheKey(key: string): boolean {
	return key.includes('|sort:trending|') || key.includes('|sort:newest|');
}

export function clearBrowseViewCache(): void {
	entries.clear();
}

export function resetBrowseViewCacheForTest(): void {
	clearBrowseViewCache();
}
